import { graphql, useFragment } from "react-relay";

import type { hooksHomepageMidPageRecommendationConfigQuery$key } from "scmp-app/queries/__generated__/hooksHomepageMidPageRecommendationConfigQuery.graphql";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Fail-closed: a missing/malformed config disables the AI slots (queue-driven)
 * — log a warning so a bad CMS payload is observable in monitoring.
 */
const parseMidPageConfig = (
  rawConfig: unknown,
): HomepageMidPageRecommendationAppConfig | undefined => {
  if (rawConfig === undefined || rawConfig === null) return undefined;

  if (!isRecord(rawConfig)) {
    console.warn(
      "[home-mid-page-config] Unexpected config shape — falling back to queues.",
      rawConfig,
    );
    return undefined;
  }

  return rawConfig as HomepageMidPageRecommendationAppConfig;
};

/**
 * App-config for SCMPWEB-9641: per-edition switch deciding whether the
 * homepage mid-page story-package slots are driven by the AI recommendation
 * API (enabled=true) or overridden by the CMS node queues
 * (homepage_package_top_{edition} / homepage_package_top_2_{edition})
 * (enabled=false).
 *
 * Fail-closed: a missing/malformed config disables the AI slots (queue-driven).
 *
 * `editionKey` is the route edition as a plain string ("asia" | "hk" | "int");
 * `Edition` enum values are the same strings as the CMS config keys.
 */
export const useHomepageMidPageRecommendationConfig = (
  reference: hooksHomepageMidPageRecommendationConfigQuery$key,
  editionKey: string,
) => {
  const data = useFragment(
    graphql`
      fragment hooksHomepageMidPageRecommendationConfigQuery on Query {
        homepageMidPageRecommendation: appConfig(
          filter: { entityId: "homepage_mid_page_recommendation" }
        ) {
          json
        }
      }
    `,
    reference,
  );

  const config = parseMidPageConfig(data.homepageMidPageRecommendation?.json);

  return {
    isStoryPackageOneAiEnabled:
      config?.storyPackageOneRecommendation?.enabled?.[editionKey] ?? false,
    isStoryPackageTwoAiEnabled:
      config?.storyPackageTwoRecommendation?.enabled?.[editionKey] ?? false,
  };
};

type HomepageMidPageRecommendationAppConfig = Partial<
  Record<HomepageMidPageRecommendationAppConfigSlot, { enabled?: Partial<Record<string, boolean>> }>
>;

type HomepageMidPageRecommendationAppConfigSlot =
  | "storyPackageOneRecommendation"
  | "storyPackageTwoRecommendation";
